/**
 * Pure helpers backing the Asana-style dependency picker in the WBS
 * detail panel (`DependencyChips.tsx`).
 *
 * The popover needs to show a filtered list of WBS items the current row
 * can legally add as a predecessor. We exclude:
 *   - the row itself
 *   - rows already listed in `dependsOn`
 *   - tree ancestors (a row depending on its parent is meaningless and
 *     leads to confusing rollup behavior)
 *   - tree descendants (same reasoning, mirror direction)
 *   - any row whose addition would close a cycle in the `dependsOn` graph
 *     (delegated to the existing `wouldCreateCycle` from `cycle.ts`)
 *
 * The query filter is a simple case-insensitive substring match across the
 * fields a user is most likely to recall (sortKey / name / assignee).
 *
 * Kept separate from the React component so the cycle / search rules can be
 * unit-tested without a DOM.
 */

import type { AssigneeDoc, WbsItemDoc } from '@mct/shared';
import { wouldCreateCycle, type TaskGraphNode } from './cycle';

/** Shape consumed by the popover row renderer. */
export interface WbsDepCandidate {
  itemId: string;
  sortKey: string;
  name: string;
  kind: WbsItemDoc['kind'];
  status: WbsItemDoc['status'];
  /** Resolved assignee display name, or '' when unassigned / unresolved. */
  assigneeName: string;
}

function toCandidate(
  i: WbsItemDoc,
  resolveName: (assigneeId: string | null) => string,
): WbsDepCandidate {
  return {
    itemId: i.itemId,
    sortKey: i.sortKey,
    name: i.name,
    kind: i.kind,
    status: i.status,
    assigneeName: resolveName(i.assigneeId ?? null),
  };
}

/** Walk `parentItemId` upward from `itemId`, exclusive of itself. */
export function collectAncestors(
  items: readonly WbsItemDoc[],
  itemId: string,
): Set<string> {
  const byId = new Map(items.map((i) => [i.itemId, i] as const));
  const out = new Set<string>();
  let cur = byId.get(itemId)?.parentItemId ?? null;
  // `parentItemId` cycles can't really happen (the move validator blocks
  // them) but the bounded-by-size guard keeps a corrupt seed from looping
  // forever in the picker.
  let safety = items.length + 1;
  while (cur && safety-- > 0) {
    out.add(cur);
    cur = byId.get(cur)?.parentItemId ?? null;
  }
  return out;
}

/** All transitive children of `itemId`, exclusive of itself. */
export function collectDescendants(
  items: readonly WbsItemDoc[],
  itemId: string,
): Set<string> {
  const byParent = new Map<string, WbsItemDoc[]>();
  for (const i of items) {
    const p = i.parentItemId ?? '';
    const arr = byParent.get(p);
    if (arr) arr.push(i);
    else byParent.set(p, [i]);
  }
  const out = new Set<string>();
  const stack: string[] = [itemId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    for (const child of byParent.get(id) ?? []) {
      if (out.has(child.itemId)) continue;
      out.add(child.itemId);
      stack.push(child.itemId);
    }
  }
  return out;
}

/**
 * Build the legal candidate list for the dependency popover.
 *
 * `currentDeps` is passed explicitly (rather than reading `item.dependsOn`)
 * so the picker can use an in-flight value from local state — useful when a
 * user has staged additions but not yet saved.
 */
export function buildDependencyCandidates(
  item: WbsItemDoc,
  allItems: readonly WbsItemDoc[],
  currentDeps: readonly string[],
  assignees: readonly AssigneeDoc[] = [],
): WbsDepCandidate[] {
  const ancestors = collectAncestors(allItems, item.itemId);
  const descendants = collectDescendants(allItems, item.itemId);
  const already = new Set(currentDeps);

  const nameById = new Map<string, string>();
  for (const a of assignees) nameById.set(a.assigneeId, a.name);
  const resolveName = (id: string | null): string =>
    (id && nameById.get(id)) || '';

  // Map WbsItemDoc → TaskGraphNode for the shared cycle checker. The
  // function only looks at id + dependsOn, so the rename to `taskId` is a
  // pure adapter (no behavioral change).
  const graph: TaskGraphNode[] = allItems.map((i) => ({
    taskId: i.itemId,
    dependsOn: i.dependsOn,
  }));

  const out: WbsDepCandidate[] = [];
  for (const i of allItems) {
    if (i.itemId === item.itemId) continue;
    if (already.has(i.itemId)) continue;
    if (ancestors.has(i.itemId)) continue;
    if (descendants.has(i.itemId)) continue;
    // Final guard: would adding this edge close a cycle in the *current*
    // dependsOn graph? Catches diamond cases the tree-relation filters miss.
    if (wouldCreateCycle(graph, item.itemId, [...currentDeps, i.itemId])) {
      continue;
    }
    out.push(toCandidate(i, resolveName));
  }
  // Stable order: lexical on sortKey first (so "1-2" sits next to "1-3"),
  // then by name as a tiebreaker.
  out.sort((a, b) => {
    const k = a.sortKey.localeCompare(b.sortKey, 'ja');
    if (k !== 0) return k;
    return a.name.localeCompare(b.name, 'ja');
  });
  return out;
}

/**
 * Normalize a string for substring matching: NFKC folds full-width Latin
 * (Ａ-Ｚ, ０-９) and half-width katakana into their canonical half-width
 * forms, then we lowercase. Without this, a row whose name contains
 * full-width "ＭＤＭ" (typed via IME) is invisible to a half-width "MDM"
 * search query — the picker silently dropped a legitimate sibling on the
 * dev project (`ＭＤＭ仕様整理` under `横河MDM開発作業`) until this fix.
 *
 * `String.prototype.normalize` is in every browser engine we target.
 */
function foldForSearch(s: string): string {
  return s.normalize('NFKC').toLowerCase();
}

/**
 * Case-insensitive substring filter over the candidate fields a user is
 * likely to recall. Empty / whitespace-only query passes everything.
 *
 * `head` caps the result to keep the popover from rendering a thousand
 * rows; the popover spec calls for ~50 max.
 *
 * Both query and haystack are NFKC-normalized so half-width ↔ full-width
 * input doesn't hide candidates (see `foldForSearch`).
 */
export function filterCandidates(
  candidates: readonly WbsDepCandidate[],
  query: string,
  head = 50,
): WbsDepCandidate[] {
  const q = foldForSearch(query.trim());
  if (!q) return candidates.slice(0, head);
  const out: WbsDepCandidate[] = [];
  for (const c of candidates) {
    const hay = foldForSearch(`${c.sortKey} ${c.name} ${c.assigneeName}`);
    if (hay.includes(q)) out.push(c);
    if (out.length >= head) break;
  }
  return out;
}

/**
 * Mirror of {@link buildDependencyCandidates} for the successor side of
 * the panel. The picker on the successor row asks "which other items
 * should be made to depend on me?" — semantically that means "for each
 * candidate `other`, simulate adding `item.itemId` to `other.dependsOn`,
 * and exclude any candidate where the simulation is illegal".
 *
 * Exclusion mirrors the predecessor rules from `item`'s perspective:
 *   - the row itself
 *   - tree ancestors of the row (rollup-confusing in either direction)
 *   - tree descendants of the row (same)
 *   - rows whose `dependsOn` already contains `item.itemId`
 *   - rows where adding the edge would close a cycle (delegated to
 *     `wouldCreateCycle` running on the candidate, not on `item`)
 */
export function buildSuccessorCandidates(
  item: WbsItemDoc,
  allItems: readonly WbsItemDoc[],
  assignees: readonly AssigneeDoc[] = [],
): WbsDepCandidate[] {
  const ancestors = collectAncestors(allItems, item.itemId);
  const descendants = collectDescendants(allItems, item.itemId);

  const nameById = new Map<string, string>();
  for (const a of assignees) nameById.set(a.assigneeId, a.name);
  const resolveName = (id: string | null): string =>
    (id && nameById.get(id)) || '';

  const graph: TaskGraphNode[] = allItems.map((i) => ({
    taskId: i.itemId,
    dependsOn: i.dependsOn,
  }));

  const out: WbsDepCandidate[] = [];
  for (const i of allItems) {
    if (i.itemId === item.itemId) continue;
    if (ancestors.has(i.itemId)) continue;
    if (descendants.has(i.itemId)) continue;
    // Already a successor (its dependsOn already includes `item.itemId`).
    if (i.dependsOn.includes(item.itemId)) continue;
    // Would adding `item.itemId` to `i.dependsOn` close a cycle?
    if (wouldCreateCycle(graph, i.itemId, [...i.dependsOn, item.itemId])) {
      continue;
    }
    out.push(toCandidate(i, resolveName));
  }
  out.sort((a, b) => {
    const k = a.sortKey.localeCompare(b.sortKey, 'ja');
    if (k !== 0) return k;
    return a.name.localeCompare(b.name, 'ja');
  });
  return out;
}

/**
 * Status → small dot color. Mirrors the badge tones used elsewhere in the
 * panel so a row's chip color matches its detail-panel header.
 */
export function statusDotClass(status: WbsItemDoc['status']): string {
  switch (status) {
    case 'done':
      return 'bg-green-500';
    case 'in_progress':
      return 'bg-yellow-500';
    case 'blocked':
      return 'bg-red-500';
    case 'cancelled':
      return 'bg-slate-400';
    case 'not_started':
    default:
      return 'bg-slate-300';
  }
}
